"use client";

import Container from "@/components/ui/Container";
import ScrollReveal from "@/components/ui/ScrollReveal";
import Section from "@/components/ui/Section";
import SectionHeader from "@/components/ui/SectionHeader";
import { contactPageData } from "@/data";
import { cn } from "@/lib/utils";
import { LocationItemCardProps } from "@/types";
import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import LocationDetails from "../common/LocationDetails";

function LocationItemCard({ title, items, emptyText }: LocationItemCardProps) {
    const getOptimalColumnCount = (total: number) => {
        if (total <= 6) return 2;
        return 3;
    };

    const targetColumns = getOptimalColumnCount(items.length);

    const chunkItems = (list: string[], columns: number) => {
        if (!list.length) return [];
        const perCol = Math.ceil(list.length / columns);
        const result: string[][] = [];
        for (let i = 0; i < columns; i++) {
            const chunk = list.slice(i * perCol, (i + 1) * perCol);
            if (chunk.length > 0) {
                result.push(chunk);
            }
        }
        return result;
    };

    const columns = chunkItems(items, targetColumns);
    const colCount = columns.length;

    return (
        <div className="bg-brand-card rounded-[10px] p-5 sm:p-6 lg:p-7 flex flex-col justify-between h-full">
            <div>
                <div className="font-oswald uppercase text-brand-cream text-base sm:text-lg tracking-[0.08em] inline-flex items-center gap-2.5 mb-5 sm:mb-6">
                    <span className="inline-block w-2.5 h-2.5 bg-brand-green shrink-0" />
                    <span>{title}</span>
                </div>

                {colCount > 0 ? (
                    <div
                        className={cn(
                            "grid gap-4 sm:gap-5 lg:gap-6",
                            colCount === 1 && "grid-cols-1",
                            colCount === 2 && "grid-cols-1 sm:grid-cols-2",
                            colCount === 3 && "grid-cols-1 sm:grid-cols-3"
                        )}
                    >
                        {columns.map((col, colIdx) => (
                            <div key={colIdx} className="space-y-3 sm:space-y-3.5">
                                {col.map((item, itemIdx) => (
                                    <div
                                        key={itemIdx}
                                        className="group flex items-center gap-2.5 sm:gap-3 select-none transition-colors duration-200"
                                    >
                                        {/* Left side: Purple accent indicator bar */}
                                        <span
                                            className="w-[3px] h-6 sm:h-7 bg-[#76275D] shrink-0 transition-colors duration-200 group-hover:bg-[#8e2e70]"
                                            aria-hidden="true"
                                        />
                                        {/* Item Title */}
                                        <span className="font-poppins text-brand-cream/90 group-hover:text-white text-xs sm:text-sm font-normal leading-snug transition-colors duration-200">
                                            {item}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-white/40 text-sm font-poppins italic py-2">
                        {emptyText}
                    </p>
                )}
            </div>
        </div>
    );
}

export default function ServiceLocation() {
    const { serviceLocation } = contactPageData;
    const searchParams = useSearchParams();

    // Determine initial location from URL search param if valid
    const locationParam = searchParams.get("location");
    const matchedInitial = serviceLocation.locations.find(
        (loc) => loc.id.toLowerCase() === locationParam?.toLowerCase()
    );
    const defaultId = matchedInitial?.id || serviceLocation.locations[0]?.id || "mumbai";

    const [activeLocationId, setActiveLocationId] = useState<string>(defaultId);
    const [displayedLocationId, setDisplayedLocationId] = useState<string>(defaultId);
    const [animationState, setAnimationState] = useState<"enter" | "exit">("enter");

    // Sync tab when searchParam changes
    const param = searchParams.get("location");
    const [prevParam, setPrevParam] = useState<string | null>(param);
    if (param !== prevParam) {
        setPrevParam(param);
        if (param) {
            const match = serviceLocation.locations.find(
                (loc) => loc.id.toLowerCase() === param.toLowerCase()
            );
            if (match && match.id !== activeLocationId) {
                setActiveLocationId(match.id);
                setDisplayedLocationId(match.id);
                setAnimationState("enter");
            }
        }
    }

    // Sync tab on hashchange
    useEffect(() => {
        const handleHash = () => {
            const hash = window.location.hash.replace("#", "").toLowerCase();
            const match = serviceLocation.locations.find(
                (loc) => loc.id.toLowerCase() === hash
            );
            if (match) {
                setActiveLocationId(match.id);
                setDisplayedLocationId(match.id);
                setAnimationState("enter");
            }
        };

        window.addEventListener("hashchange", handleHash);
        return () => window.removeEventListener("hashchange", handleHash);
    }, [serviceLocation.locations]);

    // Scroll into view if targeted from outside with a location or hash
    useEffect(() => {
        const param = searchParams.get("location");
        const hash = typeof window !== "undefined" ? window.location.hash : "";
        if (param || hash.includes("service-location")) {
            const el = document.getElementById("service-locations");
            if (el) {
                const timer = setTimeout(() => {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 100);
                return () => clearTimeout(timer);
            }
        }
    }, [searchParams]);

    const activeLocation =
        serviceLocation.locations.find((loc) => loc.id === displayedLocationId) ||
        serviceLocation.locations[0];

    const handleLocationChange = (newId: string) => {
        if (newId === activeLocationId) return;
        setActiveLocationId(newId);
        setAnimationState("exit");

        // Keep URL in sync without triggering full page reload
        if (typeof window !== "undefined") {
            const url = new URL(window.location.href);
            url.searchParams.set("location", newId);
            url.hash = "service-locations";
            window.history.replaceState(null, "", url.toString());
        }

        setTimeout(() => {
            setDisplayedLocationId(newId);
            setAnimationState("enter");
        }, 250);
    };

    return (
        <Section id="service-locations" className="bg-brand-paper pt-10 md:pt-20 overflow-hidden scroll-mt-24">
            <Container>
                <ScrollReveal variant="fade-up" duration={700}>
                    <SectionHeader
                        title={serviceLocation.title}
                        subtitle={serviceLocation.description}
                        align="center"
                        className="mb-4 md:mb-6"
                        subtitleClassName="text-xl md:text-xl lg:text-xl"
                    />

                    {/* Location selector tabs */}
                    <div className="flex flex-wrap items-center justify-center gap-4 md:gap-4 mb-8 md:mb-6">
                        {serviceLocation.locations.map((loc) => {
                            const isActive = loc.id === activeLocationId;
                            return (
                                <button
                                    key={loc.id}
                                    type="button"
                                    onClick={() => handleLocationChange(loc.id)}
                                    className={cn(
                                        "px-5 py-2 md:px-6 md:py-2 border text-sm md:text-base font-poppins font-medium transition-all duration-300",
                                        isActive
                                            ? "border-white bg-transparent text-white ring-1 ring-white"
                                            : "border-white/30 text-white/70 hover:border-white/60 hover:text-white"
                                    )}
                                >
                                    {loc.name}
                                </button>
                            );
                        })}
                    </div>

                    {/* Animated Location Content Container */}
                    <div
                        className={cn(
                            "transition-all duration-300 ease-out transform will-change-[transform,opacity]",
                            animationState === "exit"
                                ? "opacity-0 -translate-x-12"
                                : "opacity-100 translate-x-0"
                        )}
                    >
                        {/* Top Row: Map & Contact info */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center mb-10 md:mb-12">
                            {/* Map / placeholder */}
                            <div className="w-full h-[226px] rounded-[10px] overflow-hidden relative">
                                {activeLocation?.mapEmbedUrl && (
                                    <iframe
                                        key={activeLocation.mapEmbedUrl}
                                        title={`${activeLocation.name} Map`}
                                        src={activeLocation.mapEmbedUrl}
                                        className="w-full h-full opacity-80 hover:opacity-100 transition-opacity"
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                    />
                                )}
                            </div>

                            <LocationDetails
                                address={activeLocation?.address}
                                phone={activeLocation?.phone}
                                email={activeLocation?.email}
                            />
                        </div>

                        {/* Bottom Row: Common Brands & Equipment Cards */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
                            <LocationItemCard
                                title="BRANDS"
                                items={activeLocation?.brands || []}
                                emptyText="Brands list coming soon"
                            />
                            <LocationItemCard
                                title="EQUIPMENT"
                                items={activeLocation?.equipment || []}
                                emptyText="Equipment list coming soon"
                            />
                        </div>
                    </div>
                </ScrollReveal>
            </Container>
        </Section>
    );
}
